import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {StandaloneComponent} from '../standalone.component';
import {ActivatedRoute, Router} from '@angular/router';
import {AppConstants} from '../AppConstants';
import {ParamsService} from '../params.service';
import {Logger} from '../../util/logger';
import {Observable} from 'rxjs';
import {AcceptInvitationChallenge, AuthenticationChallenge, Invitation, InvitationService, User} from '../../gen';
import {AbstractControl, FormControl, FormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {ConfigurationService} from '../ui-configuration/configuration-service';
import {AppStateService, ParamsForRouteNav} from '../app-state.service';
import {ProcessingIndicatorService} from '../processing-indicator.service';


interface JoinParams {
  accept?: string;
  decline?: string;
  skip?: string;
  token?: string;
  id?: string;
  email?: string;
  next?: string;
}

@Component({
  selector: 'app-join',
  templateUrl: './join.component.html',
  styleUrls: ['./join.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class JoinComponent extends StandaloneComponent implements OnInit, OnDestroy {

  private destroyed = false;
  requiredParametersMissing = false;
  parameterMismatch = false;
  /* null, or if mismatched the value of users session email */
  emailMismatch: string = null;
  loadInvitationFailed = false;
  acceptFailed = false;
  declineFailed = false;
  invitation: Invitation;
  remainingInvitations: AuthenticationChallenge[];
  totalInvitations: number = 0;
  currentInvitationIndex: number = 0;
  private params: JoinParams;
  invitationForm = new FormGroup({
    token: new FormControl('', []),
    email: new FormControl('', [Validators.required, Validators.email]),
  });
  showAccepted: boolean;
  byToken: boolean;
  constructor(
    route: ActivatedRoute,
    router: Router,
    hostElement: ElementRef,
    private appStateService: AppStateService,
    private paramsService: ParamsService,
    private translate: TranslateService,
    private logger: Logger,
    private processingService: ProcessingIndicatorService,
    @Inject('InvitationApi') private invitationService: InvitationService,
    configuration: ConfigurationService
  ) {
    super(route, router, hostElement, configuration);
    this.idOrTokenRequiredValidator = this.idOrTokenRequiredValidator.bind(this);
    this.invitationLoadFailed = this.invitationLoadFailed.bind(this);
    this.invitationLoaded = this.invitationLoaded.bind(this);
    this.resolveField('token').setValidators([this.idOrTokenRequiredValidator]);
  }
  continueToNext() {
    const t: ParamsForRouteNav|string = this.appStateService.getAppState().resolveNextRoute(this.byToken ? [
        AppConstants.QP_EMAIL,
        AppConstants.QP_LOGIN_HINT,
        AppConstants.QP_INVITATION_ID,
        AppConstants.QP_INVITATION_TOKEN,
        AppConstants.QP_HANDLE_CHALLENGE,
      ] : this.params.token ? [
        AppConstants.QP_LOGIN_HINT,
        AppConstants.QP_INVITATION_ID
      ] : [
        AppConstants.QP_EMAIL,
        AppConstants.QP_LOGIN_HINT,
        AppConstants.QP_INVITATION_ID
      ])
    ;
    this.logger.debug('Invitation completed: %o', t);
    if (typeof t === 'string') {
      this.processingService.show();
      window.location.href = t as string;
    } else if (t) {
      this.router.navigate([t.path], {
        queryParams: t.queryParams,
        fragment: t.fragment,
      });
    }
  }
  idOrTokenRequiredValidator(ctrl: FormControl) {
    let retVal = null;
    if (!this.resolveField('token').value && (!this.params || !this.params.id)) {
      retVal = { 'idOrTokenRequired': {value: ctrl.value} };
    }
    return retVal;
  }
  ngOnInit() {
    super.init();
    this.remainingInvitations = this.appStateService.getAppState().getAuthenticationState().getRemainingChallenges()
      .filter((c) => c.authenticationMethod === AppConstants.AC_AM_INVITATION)
    ;
    this.currentInvitationIndex = this.appStateService.getAppState().getAuthenticationState().getAcceptedInvitations().length +
      this.appStateService.getAppState().getAuthenticationState().getDeclinedInvitations().length +
      this.appStateService.getAppState().getAuthenticationState().getSkippedInvitations().length;
    this.totalInvitations = this.remainingInvitations.length + this.currentInvitationIndex;
    this.readParams().then((val) => {
      this.params = val;
      if (val) {
        // if one has already been marked as accepted, and there are more than one remaining, we count that as handled and increase
        // the index.
        this.currentInvitationIndex = this.currentInvitationIndex + (!!val.accept && this.remainingInvitations.length > 1 ? 1 : 0);
        if (val.token) {
          this.resolveField('token').setValue(val.token);
        }
        let hasEmail;
        if (val.email) {
          hasEmail = true;
          this.resolveField('email').setValue(val.email);
        } else if (this.appStateService.getAppState().getAuthenticationState().hasSession()) {
          const user: User = this.appStateService.getAppState().getAuthenticationState().getProfile();
          if (user && user.email) {
            hasEmail = true;
            this.resolveField('email').setValue(user.email);
          }
        }
        if (val.token && hasEmail) {
          this.invitationService.getInvitationByTokenForm(
            this.resolveField('token').value,
            this.resolveField('email').value,
            'response',
            false
          ).subscribe((response) => {
            if (this.remainingInvitations.length > 1) {
              const others = this.remainingInvitations.map((c) => {
                return (c as AcceptInvitationChallenge).invitationId;
              }).filter((c) => {
                return c !== response.body.id;
              });
              if (others.length > 0) {
                this.loadInvitation(others[0]);
              } else {
                this.byToken = true;
                this.invitationLoaded(response.body);
                if (val.accept === this.params.token) {
                  this.accept();
                }
              }
            } else {
              this.byToken = true;
              this.invitationLoaded(response.body);
              if (val.accept === this.params.token) {
                this.accept();
              }
            }
          }, this.invitationLoadFailed);

        } else if (val.id) {
          this.loadInvitation(val.id);
        } else {
          this.requiredParametersMissing = true;
        }
        this.resolveField('token').updateValueAndValidity();
        this.invitationForm.updateValueAndValidity();
      }
    });
  }
  private loadInvitation(id, callback?: (inv: Invitation | any) => void) {
    if (id) {
      this.invitationService.getInvitationById(id, 'response', false)
        .subscribe((inv) => {
          if (callback) {
            callback(inv.body as Invitation);
          } else {
            this.invitationLoaded(inv.body as Invitation);
          }
        }, (err) => {
          if (callback) {
            callback(err);
          } else {
            this.invitationLoadFailed(err);
          }
        })
      ;
    } else {
      this.invitationLoadFailed({ error: 'required_parameters_missing' });
    }
  }
  private invitationLoaded(inv: Invitation) {
    if (!this.destroyed) {
      this.logger.debug('invitation loaded: %o', inv);
      this.invitation = inv;
      if (this.params.id && this.params.id !== this.invitation.id) {
        this.parameterMismatch = true;
      }
      if (this.appStateService.getAppState().getAuthenticationState().hasSession()) {
        const user: User = this.appStateService.getAppState().getAuthenticationState().getProfile();
        if (!user.email || user.email.toLowerCase() !== inv.email.toLowerCase()) {
          this.emailMismatch = user.email || 'undefined';
          if (user.email) {
            this.emailMismatch = user.email;
          } else {
            this.translate.get('join.undefined-email-label').subscribe((res) => {
              this.emailMismatch = res;
            }, err => {
              this.emailMismatch = 'join.undefined-email-label';
            });
          }
        }
      }
    }
  }
  private invitationLoadFailed(err) {
    this.logger.debug('loading invitation failed %o', err);
    this.loadInvitationFailed = true;
  }
  ngOnDestroy() {
    this.destroyed = true;
  }
  private resolveField(s: string): AbstractControl {
    return this.invitationForm.get(s);
  }
  private resolvePathForSession(callback: (t: string) => void, isAccept?: boolean) {
    if (this.invitation &&
      this.invitation.memberStatus !== 'user' &&
      (!!this.configuration.getProperties().registrationAvailability.find((f) => f === 'always') || isAccept)) {
      // check if local flows are allowed for the email
      this.appStateService.fetchAuthenticationState(this.invitation.email as string, (val) => {
        const localFlows = val.authenticationFlows.filter(
          (f) => !f.disabled && f.challenges?.[0]?.authenticationMethod !== AppConstants.AC_AM_DELEGATED);
        if (localFlows.length > 0) {
          // local auth flows allowed, user not a member so we redirect to registration
          callback(AppConstants.PATH_REGISTER);
        } else {
          // local auth flows not allowed, user not a member, sow e redirect to login (for federated login)
          callback(AppConstants.PATH_LOGIN);
        }
      });
    } else {
      callback(AppConstants.PATH_LOGIN);
    }
  }
  signOut() {
    this.router.navigate([AppConstants.PATH_LOGOUT], {
      relativeTo: this.route,
    });
  }
  close() {
    if (!this.appStateService.getAppState().getAuthenticationState().isFullyAuthenticated()) {
      const qp = {
        [AppConstants.QP_INVITATION_TOKEN]: null,
        [AppConstants.QP_INVITATION_ID]: null,
        [AppConstants.QP_EMAIL]: this.resolveField('email').value ? this.resolveField('email').value : null,
        [AppConstants.QP_LOGIN_HINT]: undefined,
      };
      this.resolvePathForSession((t: string) => {
        if (t === AppConstants.PATH_LOGIN && !!this.invitation && this.invitation.memberStatus !== 'user') {
          // redirect will be to login, but we know the user does not have an account so we can clear the email
          qp[AppConstants.QP_EMAIL] = null;
        }
        this.router.navigate([t], {
          relativeTo: this.route,
          queryParamsHandling: 'merge',
          queryParams: qp,
        });
      });
    } else if (this.requiredParametersMissing || this.loadInvitationFailed || this.emailMismatch) {
      // App.component triggers a redirect to next before this is hit, when applicable.
      // Hopefully always, but at least in all the test cases. This redirect should probably be conditional,
      // but we don't have proper data here at the moment.
      this.continueToNext();
    }
  }
  accept() {
    this.logger.debug('accept');
    // we sometimes increase the index by 1 for improved ux, so we need to take that into account when resolving this
    const showWelcomePageLink =
      (this.currentInvitationIndex - (!!this.params.accept && this.remainingInvitations.length > 1 ? 1 : 0) !== this.totalInvitations - 1) ||
      (!!this.invitation.invitationCompletedUrl && !!this.params.next && this.params.next !== this.invitation.invitationCompletedUrl) ||
      (!this.params.next && !!this.invitation.invitationCompletedUrl)
    ;
    if (this.appStateService.getAppState().getAuthenticationState().hasSession()) {
      if (!this.byToken && this.invitation?.id) {
        this.invitationService.acceptInvitationById(this.invitation.id, 'response', false)
          .subscribe(() => {
            this.appStateService.getAppState().getAuthenticationState().acceptInvitation(this.invitation.id);
            if (!showWelcomePageLink) {
              this.continueToNext();
            } else {
              this.showAccepted = true;
            }
          }, (err) => {
            this.acceptFailed = true;
          })
        ;
      } else if (this.byToken) {
        this.invitationService.acceptInvitationForm(
          this.resolveField('token').value,
          this.resolveField('email').value,
          'response',
          false
        ).subscribe((inv) => {
          this.appStateService.getAppState().getAuthenticationState().acceptInvitation((inv.body as Invitation).id);
          // successful accept may validate email, so we need to reload the state
          this.appStateService.reloadAuthenticationState(() => {
            if (!showWelcomePageLink) {
              this.continueToNext();
            } else {
              this.showAccepted = true;
            }
          });
        }, (err) => {
          this.acceptFailed = true;
        });
      }
    } else {
      this.invitationService.getInvitationByTokenForm(
        this.resolveField('token').value,
        this.resolveField('email').value,
        'response',
        false
      ).subscribe((inv) => {
        this.params.accept = null;
        this.invitationLoaded(inv.body as Invitation);
        const qp = {
          [AppConstants.QP_REQUIRE_CHALLENGE]: AppConstants.AC_AM_INVITATION_ACCEPTED,
          [AppConstants.QP_INVITATION_TOKEN]: this.resolveField('token').value,
          [AppConstants.QP_EMAIL]: this.resolveField('email').value,
          [AppConstants.QP_LOGIN_HINT]: undefined,
        };
        this.resolvePathForSession((t: string) => {
          this.router.navigate([t], {
            relativeTo: this.route,
            queryParamsHandling: 'merge',
            queryParams: qp,
          });
        }, true);
      }, (err) => {
        this.acceptFailed = true;
      });
    }
  }
  decline() {
    this.logger.debug('decline');
    const declinedHandler = (invresponse) => {
      if (!this.appStateService.getAppState().getAuthenticationState().hasSession()) {
        const qp = {
          [AppConstants.QP_INVITATION_ID]: null,
          [AppConstants.QP_INVITATION_TOKEN]: null,
          [AppConstants.QP_EMAIL]: this.resolveField('email').value,
          [AppConstants.QP_LOGIN_HINT]: undefined,
        };
        if (!!invresponse.body.invitationCompletedUrl &&
          !!this.params.next &&
          this.params.next === invresponse.body.invitationCompletedUrl) {
          qp[AppConstants.QP_NEXT] = undefined;
        }
        this.resolvePathForSession((t: string) => {
          if (t === AppConstants.PATH_LOGIN && this.invitation.memberStatus !== 'user') {
            // redirect will be to login, but we know the user does not have an account so we can clear the email
            qp[AppConstants.QP_EMAIL] = null;
          }
          this.router.navigate([t], {
            relativeTo: this.route,
            queryParamsHandling: 'merge',
            queryParams: qp,
          });
        });
      } else {
        this.appStateService.getAppState().getAuthenticationState().declineInvitation((invresponse.body as Invitation).id);
        this.continueToNext();
      }
    };
    if (!this.byToken && this.invitation.id) {
      this.invitationService.declineInvitationById(this.invitation.id, 'response', false)
        .subscribe(declinedHandler, (err) => { this.declineFailed = true; })
      ;
    } else if (this.byToken) {
      this.invitationService.declineInvitationForm(
        this.resolveField('token').value,
        this.resolveField('email').value,
        'response',
        false
      ).subscribe(declinedHandler, (err) => { this.declineFailed = true; });
    }
  }
  skip() {
    this.logger.debug('skip');
    if (this.invitation) {
      this.appStateService.getAppState().getAuthenticationState().skipInvitation(this.invitation.id);
      if (!this.appStateService.getAppState().getAuthenticationState().hasSession()) {
        const qp = {
          [AppConstants.QP_INVITATION_TOKEN]: null,
          [AppConstants.QP_INVITATION_ID]: null,
          [AppConstants.QP_EMAIL]: this.resolveField('email').value,
          [AppConstants.QP_LOGIN_HINT]: undefined,
        };
        this.resolvePathForSession((t: string) => {
          if (t === AppConstants.PATH_LOGIN && this.invitation.memberStatus !== 'user') {
            // redirect will be to login, but we know the user does not have an account so we can clear the email
            qp[AppConstants.QP_EMAIL] = null;
          }
          this.router.navigate([t], {
            relativeTo: this.route,
            queryParamsHandling: 'merge',
            queryParams: qp,
          });
        });
      } else {
        this.continueToNext();
      }
    }
  }
  allowSkip(): boolean {
    return this.configuration.getProperties().allowSkipInvitation === true;
  }
  invitationCopy(): string {
    return !this.requiredParametersMissing && this.invitation ? this.invitation.invitationScopeInformation : null;
  }
  private readParams(): Promise<JoinParams> {
    return new Promise<JoinParams>((resolve) => {
      const acceptPromise = this.paramsService.getParam(AppConstants.QP_HANDLE_CHALLENGE);
      const tokenPromise = this.paramsService.getParam(AppConstants.QP_INVITATION_TOKEN);
      const emailPromise = this.paramsService.getParam(AppConstants.QP_EMAIL);
      const idPromise = this.paramsService.getParam(AppConstants.QP_INVITATION_ID);
      const nextPromise = this.paramsService.getParam(AppConstants.QP_NEXT);
      Promise.all([
        acceptPromise, tokenPromise, emailPromise, idPromise, nextPromise
      ]).then((values) => {
        const result: JoinParams = {
          accept: values[0] === AppConstants.AC_AM_INVITATION_ACCEPTED ? values[1] : null,
          token: values[1],
          email: values[2],
          id: values[3],
          next: values[4],
        };

        resolve(result);
      });
    });
  }
  public resolveWindowTitlePart(): Observable<string|undefined> {
    return this.translate.get('join.window-title');
  }
}
